'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, Dog, Heart, Trophy, Star, CreditCard, ShieldAlert, Settings, Bell, ChevronLeft, ChevronRight, LogOut, Users, BarChart3, Download } from 'lucide-react';

const NAV_GROUPS = [
  {
    label: 'MY ACCOUNT',
    items: [
      { href: '/user-dashboard', icon: LayoutDashboard, label: 'Dashboard', badge: null },
      { href: '/my-dogs-management', icon: Dog, label: 'My Dogs', badge: '3' },
      { href: '/user-dashboard', icon: Bell, label: 'Notifications', badge: '5' },
      { href: '/user-dashboard', icon: Heart, label: 'Following', badge: null },
      { href: '/user-dashboard', icon: Download, label: 'Downloads', badge: null },
      { href: '/billing', icon: CreditCard, label: 'Billing', badge: null },
      { href: '/user-dashboard', icon: Settings, label: 'Settings', badge: null },
    ],
  },
  {
    label: 'ADMIN',
    items: [
      { href: '/admin-dashboard', icon: BarChart3, label: 'Admin Dashboard', badge: null },
      { href: '/moderation-center', icon: ShieldAlert, label: 'Moderation', badge: '12' },
      { href: '/admin-dashboard', icon: Trophy, label: 'Hall of Fame', badge: null },
      { href: '/admin-dashboard', icon: Star, label: 'Dog of the Day', badge: null },
      { href: '/admin-dashboard', icon: Users, label: 'Members', badge: null },
    ],
  },
];

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const { user, signOut, getUserProfile } = useAuth();
  const [profile, setProfile] = useState<any>(null);

  useEffect(() => {
    if (user) {
      getUserProfile().then(setProfile).catch(() => null);
    }
  }, [user]);

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/login');
    } catch {}
  };

  const displayName = profile?.full_name || user?.email?.split('@')[0] || 'User';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const planLabel = profile?.plan
    ? profile.plan.charAt(0).toUpperCase() + profile.plan.slice(1) + 'Member' :'Member';

  return (
    <aside
      className={`sidebar-transition relative flex flex-col bg-card border-r border-border shrink-0 ${collapsed ? 'w-16' : 'w-60'} min-h-screen z-30`}
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-5 border-b border-border ${collapsed ? 'justify-center' : ''}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-extrabold text-base text-foreground tracking-tight leading-tight">
            DoggyYears
          </span>
        )}
      </div>
      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 scrollbar-thin">
        {NAV_GROUPS?.map((group) => (
          <div key={`group-${group?.label}`} className="mb-4">
            {!collapsed && (
              <p className="px-4 py-1 text-[10px] font-700 tracking-widest text-muted-foreground uppercase mb-1">
                {group?.label}
              </p>
            )}
            {group?.items?.map((item) => {
              const isActive = pathname === item?.href || (item?.href !== '/user-dashboard' && pathname?.startsWith(item?.href));
              return (
                <Link
                  key={`nav-${item?.label}`}
                  href={item?.href}
                  title={collapsed ? item?.label : undefined}
                  className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg text-sm font-500 transition-all duration-150 group relative
                    ${isActive
                      ? 'bg-primary/10 text-primary' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                >
                  <item.icon size={18} className="shrink-0" />
                  {!collapsed && <span className="truncate">{item?.label}</span>}
                  {!collapsed && item?.badge && (
                    <span className="ml-auto text-[10px] font-700 bg-primary text-primary-foreground rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                      {item?.badge}
                    </span>
                  )}
                  {collapsed && item?.badge && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
                  )}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>
      {/* User profile */}
      <div className={`border-t border-border p-4 ${collapsed ? 'flex justify-center' : ''}`}>
        {!collapsed ? (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full gradient-violet flex items-center justify-center shrink-0">
              <span className="text-white text-xs font-700">{initials}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-600 text-foreground truncate">{displayName}</p>
              <p className="text-xs text-muted-foreground truncate">{planLabel}</p>
            </div>
            <button
              onClick={handleSignOut}
              className="text-muted-foreground hover:text-danger transition-colors"
              title="Sign out"
            >
              <LogOut size={15} />
            </button>
          </div>
        ) : (
          <button
            onClick={handleSignOut}
            className="w-8 h-8 rounded-full gradient-violet flex items-center justify-center"
            title="Sign out"
          >
            <span className="text-white text-xs font-700">{initials}</span>
          </button>
        )}
      </div>
      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 bg-card border border-border rounded-full flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-all shadow-card z-10"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}